import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  Building2,
  Eye,
  Users,
  PenLine,
  Trash2,
  X,
  Loader2,
  ShoppingCart,
  Package
} from 'lucide-react';
import {
  getAllCompanies,
  getCompanyOrders,
  getCompanyDetails,
  getCompanyClients,
  updateCompanyBySuperAdmin,
  deleteCompanyBySuperAdmin
} from '../../../redux/api';
import { formatInvoiceEur } from '../../../utils/invoiceDisplayFormat';
import { formatBillingYmdLocal } from '../../../utils/billingDateFormat';
import { packageLabelSr, packageBadgeClass, resolveCompanyPackageNumber } from '../../../utils/packageDisplay';

const CompaniesListView = () => {
  const { user } = useSelector((state) => state.auth);
  const isSuperAdmin = user?.result?.role === 'superadmin';
  const [loading, setLoading] = useState(true);
  const [companies, setCompanies] = useState([]);
  const [editingCompany, setEditingCompany] = useState(null);
  const [deletingCompany, setDeletingCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [loadingCompanyDetails, setLoadingCompanyDetails] = useState(false);
  const [viewingClients, setViewingClients] = useState(null);
  const [companyClients, setCompanyClients] = useState(null);
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientsPage, setClientsPage] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyOrders, setCompanyOrders] = useState(null);
  const [loadingOrders, setLoadingOrders] = useState(false);

  useEffect(() => {
    console.log('✅ CompaniesListView: Component mounted/updated');
    console.log('  - isSuperAdmin:', isSuperAdmin);
    console.log('  - Current URL:', window.location.pathname);
    console.log('  - User:', user?.result?.email);
    
    if (!isSuperAdmin) {
      toast.error('Nemate pristup ovoj stranici');
      return;
    }

    fetchData();
  }, [isSuperAdmin]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const companiesResponse = await getAllCompanies();
      setCompanies(companiesResponse.data);
    } catch (error) {
      console.error('Error fetching companies data:', error);
      toast.error('Greška pri učitavanju podataka');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('sr-RS', {
      style: 'currency',
      currency: 'RSD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6">
        <p className="text-red-500">Nemate pristup ovoj stranici.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  console.log('✅ CompaniesListView: Rendering component');
  console.log('  - Companies count:', companies.length);
  console.log('  - Loading:', loading);

  return (
    <div className="p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-6 w-6 text-blue-500" />
            <h1 className="text-2xl font-bold text-white">Lista kompanija</h1>
          </div>
          <p className="text-gray-400">Upravljanje registrovanim kompanijama</p>
        </div>

        {/* Companies List */}
        <div className="bg-gray-800 rounded-lg p-4 sm:p-6 border border-gray-700">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Br.</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Kompanija</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Email</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Paket</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Cena / mes</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Zaduženje</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Status</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Klijenti</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Poslovi</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Rezervni delovi</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Radnici</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Partneri</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Registrovan</th>
                  <th className="text-left py-3 px-2 text-gray-400 text-sm font-medium">Akcije</th>
                </tr>
              </thead>
              <tbody>
                {companies.map((company) => {
                  const deleted = Boolean(company.isDeleted);
                  return (
                    <tr
                      key={company._id}
                      className={`border-b border-gray-700 ${
                        deleted
                          ? 'bg-black/40 text-gray-500 opacity-50'
                          : 'hover:bg-gray-700/50'
                      }`}
                    >
                      <td className={`py-3 px-2 font-mono text-xs ${deleted ? 'text-gray-500' : 'text-gray-300'}`}>
                        {company.companyNumber ? `Spintasker ${company.companyNumber}` : '—'}
                      </td>
                      <td className={`py-3 px-2 ${deleted ? 'text-gray-500' : 'text-white'}`}>
                        {company.companyName}
                        {deleted ? (
                          <span className="ml-2 rounded bg-gray-700 px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-gray-400">
                            Obrisana
                          </span>
                        ) : null}
                      </td>
                      <td className={`py-3 px-2 ${deleted ? 'text-gray-500' : 'text-gray-300'}`}>{company.email}</td>
                      <td className="py-3 px-2">
                        <span className={`px-2 py-1 rounded text-xs ${packageBadgeClass(company)}`}>
                          {packageLabelSr(company)}
                        </span>
                        {company.billing?.firstPaidPlanChangeAt && (
                          <p className="text-[10px] text-gray-500 mt-0.5">
                            plaćeni od{' '}
                            {new Date(company.billing.firstPaidPlanChangeAt).toLocaleDateString('sr-RS')}
                          </p>
                        )}
                      </td>
                      <td className="py-3 px-2 text-white font-mono text-xs">
                        {company.billing?.monthlyEur > 0
                          ? `${formatInvoiceEur(company.billing.monthlyEur)} EUR`
                          : '—'}
                      </td>
                      <td className="py-3 px-2 text-white font-mono text-xs">
                        {company.billing?.hasOutstanding ? (
                          <div>
                            <span className="text-amber-300">
                              {formatInvoiceEur(company.billing.outstandingEur)} EUR
                            </span>
                            {company.billing.currentPeriodAccrualEur > 0 &&
                              company.billing.currentPeriodStartYmd && (
                                <p className="text-[10px] text-gray-500 mt-0.5">
                                  tekući period{' '}
                                  {formatBillingYmdLocal(company.billing.currentPeriodStartYmd, 'sr-RS')} —{' '}
                                  {formatBillingYmdLocal(company.billing.currentPeriodEndYmd, 'sr-RS')}
                                </p>
                              )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="py-3 px-2">
                        {company.isActive === true ? (
                          <span className="px-2 py-1 bg-green-600 text-white rounded text-xs">Aktivan</span>
                        ) : (
                          <span className="px-2 py-1 bg-gray-600 text-gray-300 rounded text-xs">Neaktivan</span>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="px-2 py-1 bg-purple-600 text-white rounded text-xs font-semibold">
                          {company.clientCount || 0}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="px-2 py-1 bg-blue-600 text-white rounded text-xs font-semibold">
                          {company.totalJobsCount || 0}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="px-2 py-1 bg-amber-600 text-white rounded text-xs font-semibold">
                          {company.sparePartsCount || 0}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="px-2 py-1 bg-cyan-600 text-white rounded text-xs font-semibold">
                          {company.workersCount || 0}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <span className="px-2 py-1 bg-emerald-600 text-white rounded text-xs font-semibold">
                          {company.partnersCount || 0}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-gray-300">
                        {company.createdAt ? new Date(company.createdAt).toLocaleDateString('sr-RS', { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit'
                        }) : 
                        company.created ? new Date(company.created).toLocaleDateString('sr-RS', { 
                          year: 'numeric', 
                          month: '2-digit', 
                          day: '2-digit'
                        }) : '-'}
                      </td>
                      <td className="py-3 px-2">
                        {deleted ? (
                          <span className="text-xs text-gray-500">—</span>
                        ) : (
                        <div className="flex items-center gap-1">
                          <button
                            onClick={async () => {
                              setSelectedCompany(company);
                              setLoadingOrders(true);
                              try {
                                const response = await getCompanyOrders(company._id);
                                setCompanyOrders(response.data);
                              } catch (error) {
                                console.error('Error loading company orders:', error);
                                toast.error(error.response?.data?.message || 'Greška pri učitavanju narudžbina');
                                setCompanyOrders(null);
                              } finally {
                                setLoadingOrders(false);
                              }
                            }}
                            className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
                            title="Pregled narudžbina"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              setViewingClients(company);
                              setLoadingClients(true);
                              setClientsPage(1);
                              try {
                                const response = await getCompanyClients(company._id, 1, 100);
                                setCompanyClients(response.data);
                              } catch (error) {
                                console.error('Error loading company clients:', error);
                                toast.error(error.response?.data?.message || 'Greška pri učitavanju klijenata');
                                setCompanyClients(null);
                              } finally {
                                setLoadingClients(false);
                              }
                            }}
                            className="p-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
                            title="Pregled klijenata"
                          >
                            <Users className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingCompany(company)}
                            className="p-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded transition-colors"
                            title="Izmeni kompaniju"
                          >
                            <PenLine className="w-4 h-4" />
                          </button>
                          <button
                            onClick={async () => {
                              setLoadingCompanyDetails(true);
                              try {
                                const response = await getCompanyDetails(company._id);
                                setCompanyDetails(response.data);
                                setDeletingCompany(company);
                              } catch (error) {
                                console.error('Error loading company details:', error);
                                toast.error('Greška pri učitavanju detalja kompanije');
                              } finally {
                                setLoadingCompanyDetails(false);
                              }
                            }}
                            className="p-2 bg-red-600 hover:bg-red-700 text-white rounded transition-colors"
                            title="Obriši kompaniju"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Edit Company Modal */}
        {editingCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-md w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-white">Izmeni kompaniju</h3>
                <button
                  onClick={() => setEditingCompany(null)}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Naziv kompanije</label>
                  <input
                    type="text"
                    value={editingCompany.companyName || ''}
                    onChange={(e) => setEditingCompany({ ...editingCompany, companyName: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Email</label>
                  <input
                    type="email"
                    value={editingCompany.email || ''}
                    onChange={(e) => setEditingCompany({ ...editingCompany, email: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm text-gray-300 mb-1">Paket</label>
                  <select
                    value={resolveCompanyPackageNumber(editingCompany)}
                    onChange={(e) =>
                      setEditingCompany({
                        ...editingCompany,
                        package: Number(e.target.value),
                      })
                    }
                    className="w-full px-3 py-2 bg-gray-700 text-white rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={1}>Besplatni</option>
                    <option value={2}>Starter</option>
                    <option value={3}>Standardni</option>
                    <option value={4}>Biznis</option>
                    <option value={5}>Premium</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={editingCompany.subscriptionActive || false}
                      onChange={(e) => setEditingCompany({ ...editingCompany, subscriptionActive: e.target.checked })}
                      className="w-4 h-4 text-blue-600 bg-gray-700 border-gray-600 rounded focus:ring-blue-500"
                    />
                    Aktivan
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <button
                    onClick={() => setEditingCompany(null)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                  >
                    Otkaži
                  </button>
                  <button
                    onClick={async () => {
                      try {
                        await updateCompanyBySuperAdmin({ 
                          companyId: editingCompany._id, 
                          companyData: editingCompany 
                        });
                        toast.success('Kompanija uspešno ažurirana');
                        setEditingCompany(null);
                        fetchData();
                      } catch (error) {
                        console.error('Error updating company:', error);
                        toast.error(error.response?.data?.message || 'Greška pri ažuriranju kompanije');
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                  >
                    Sačuvaj
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Company Confirmation Modal */}
        {deletingCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-lg max-w-2xl w-full p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-bold text-red-500">Potvrda brisanja kompanije</h3>
                <button
                  onClick={() => {
                    setDeletingCompany(null);
                    setCompanyDetails(null);
                  }}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingCompanyDetails ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                  <p className="text-gray-400">Učitavanje detalja...</p>
                </div>
              ) : companyDetails ? (
                <div className="space-y-4">
                  <div className="bg-red-900/20 border border-red-600 rounded-lg p-4">
                    <p className="text-red-400 font-semibold mb-2">⚠️ Upozorenje: Ova akcija je nepovratna!</p>
                    <p className="text-white text-sm">
                      Brisanjem kompanije <span className="font-bold">{companyDetails.company.companyName}</span> ({companyDetails.company.email}) 
                      biće obrisani svi podaci vezani za ovu kompaniju.
                    </p>
                  </div>

                  <div className="bg-gray-700 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Kompanija</p>
                      <p className="text-white font-semibold">{companyDetails.company.companyName}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Email</p>
                      <p className="text-white">{companyDetails.company.email}</p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-sm mb-1">Vlasnik</p>
                      <p className="text-white">{companyDetails.company.ownerName || 'N/A'}</p>
                    </div>
                    <div className="border-t border-gray-600 pt-3 mt-3">
                      <p className="text-gray-400 text-sm mb-2">Statistika:</p>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-gray-400 text-xs">Broj klijenata</p>
                          <p className="text-white font-bold text-lg">{companyDetails.clientsCount}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">Završeni poslovi</p>
                          <p className="text-white font-bold text-lg">{companyDetails.completedJobsCount}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 text-xs">Ukupno poslova</p>
                          <p className="text-white font-bold text-lg">{companyDetails.totalJobsCount}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {companyDetails.clientsCount > 0 && (
                    <div className="bg-gray-700 rounded-lg p-4 max-h-60 overflow-y-auto">
                      <p className="text-gray-400 text-sm mb-2">Lista klijenata ({companyDetails.clients.length} od {companyDetails.clientsCount}):</p>
                      <div className="space-y-2">
                        {companyDetails.clients.map((client, index) => {
                          // Format address - handle both string and object formats
                          let addressText = '-';
                          if (client.address) {
                            if (typeof client.address === 'string') {
                              addressText = client.address;
                            } else if (typeof client.address === 'object') {
                              const parts = [];
                              if (client.address.street) parts.push(client.address.street);
                              if (client.address.number) parts.push(client.address.number);
                              if (client.address.floor) parts.push(`Spr. ${client.address.floor}`);
                              if (client.address.apartment) parts.push(`Br. ${client.address.apartment}`);
                              addressText = parts.length > 0 ? parts.join(', ') : '-';
                            }
                          }
                          
                          return (
                            <div key={index} className="bg-gray-600 rounded p-2 text-sm">
                              <p className="text-white font-semibold">{client.name || 'Nepoznat klijent'}</p>
                              {client.email && <p className="text-gray-300 text-xs">{client.email}</p>}
                              {client.phone && <p className="text-gray-300 text-xs">{client.phone}</p>}
                              {addressText !== '-' && <p className="text-gray-300 text-xs">{addressText}</p>}
                            </div>
                          );
                        })}
                        {companyDetails.clientsCount > companyDetails.clients.length && (
                          <p className="text-gray-400 text-xs italic">
                            ... i još {companyDetails.clientsCount - companyDetails.clients.length} klijenata
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-4">
                    <button
                      onClick={() => {
                        setDeletingCompany(null);
                        setCompanyDetails(null);
                      }}
                      className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                    >
                      Otkaži
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          await deleteCompanyBySuperAdmin(deletingCompany._id);
                          toast.success('Kompanija uspešno obrisana');
                          setDeletingCompany(null);
                          setCompanyDetails(null);
                          fetchData();
                        } catch (error) {
                          console.error('Error deleting company:', error);
                          toast.error(error.response?.data?.message || 'Greška pri brisanju kompanije');
                        }
                      }}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                    >
                      Obriši kompaniju
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-400">Greška pri učitavanju detalja</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Company Clients Modal */}
        {viewingClients && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Klijenti - {viewingClients.companyName}
                  </h2>
                  {companyClients && (
                    <p className="text-gray-400 text-sm mt-1">
                      Ukupno klijenata: {companyClients.pagination?.total || 0}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setViewingClients(null);
                    setCompanyClients(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                {loadingClients ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                    <p className="text-gray-400">Učitavanje klijenata...</p>
                  </div>
                ) : companyClients ? (
                  companyClients.clients && companyClients.clients.length > 0 ? (
                    <div className="space-y-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full bg-gray-800">
                          <thead className="bg-gray-700">
                            <tr>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Ime</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Email</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Telefon</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Adresa</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Ocena</th>
                              <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Datum kreiranja</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700">
                            {companyClients.clients.map((client) => (
                              <tr key={client._id} className="hover:bg-gray-700">
                                <td className="px-4 py-2 text-white">{client.name || 'Nepoznat'}</td>
                                <td className="px-4 py-2 text-gray-300">{client.email || '-'}</td>
                                <td className="px-4 py-2 text-gray-300">{client.phone || '-'}</td>
                                <td className="px-4 py-2 text-gray-300">
                                  {client.address ? (
                                    typeof client.address === 'string' ? client.address : 
                                    `${client.address.street || ''} ${client.address.number || ''}`.trim() || '-'
                                  ) : '-'}
                                </td>
                                <td className="px-4 py-2 text-gray-300">
                                  {client.rating ? (
                                    <span className="px-2 py-1 bg-yellow-600 text-white rounded text-xs">
                                      {client.rating}/10
                                    </span>
                                  ) : '-'}
                                </td>
                                <td className="px-4 py-2 text-gray-300">
                                  {client.createdAt ? new Date(client.createdAt).toLocaleDateString('sr-RS') : '-'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      {/* Pagination */}
                      {companyClients.pagination && companyClients.pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between pt-4">
                          <button
                            onClick={async () => {
                              if (clientsPage > 1) {
                                const newPage = clientsPage - 1;
                                setClientsPage(newPage);
                                setLoadingClients(true);
                                try {
                                  const response = await getCompanyClients(viewingClients._id, newPage, 100);
                                  setCompanyClients(response.data);
                                } catch (error) {
                                  console.error('Error loading clients:', error);
                                  toast.error('Greška pri učitavanju klijenata');
                                } finally {
                                  setLoadingClients(false);
                                }
                              }
                            }}
                            disabled={clientsPage === 1}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Prethodna
                          </button>
                          <span className="text-gray-300">
                            Strana {companyClients.pagination.page} od {companyClients.pagination.totalPages}
                          </span>
                          <button
                            onClick={async () => {
                              if (clientsPage < companyClients.pagination.totalPages) {
                                const newPage = clientsPage + 1;
                                setClientsPage(newPage);
                                setLoadingClients(true);
                                try {
                                  const response = await getCompanyClients(viewingClients._id, newPage, 100);
                                  setCompanyClients(response.data);
                                } catch (error) {
                                  console.error('Error loading clients:', error);
                                  toast.error('Greška pri učitavanju klijenata');
                                } finally {
                                  setLoadingClients(false);
                                }
                              }
                            }}
                            disabled={clientsPage >= companyClients.pagination.totalPages}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Sledeća
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>Ova kompanija još nema klijenata.</p>
                    </div>
                  )
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-400">Greška pri učitavanju klijenata</p>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4 flex justify-end">
                <button
                  onClick={() => {
                    setViewingClients(null);
                    setCompanyClients(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                >
                  Zatvori
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Company Orders Modal */}
        {selectedCompany && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center z-10">
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Narudžbe - {selectedCompany.companyName}
                  </h2>
                  {companyOrders && (
                    <p className="text-gray-400 text-sm mt-1">
                      Ukupno narudžbina: {companyOrders.totalOrders} | 
                      Ukupan prihod: {formatCurrency(companyOrders.totalRevenue || 0)}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => {
                    setSelectedCompany(null);
                    setCompanyOrders(null);
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="p-6">
                {loadingOrders ? (
                  <div className="text-center py-12">
                    <Loader2 className="w-8 h-8 mx-auto mb-4 animate-spin text-blue-500" />
                    <p className="text-gray-400">Učitavanje narudžbina...</p>
                  </div>
                ) : companyOrders ? (
                  companyOrders.webshop ? (
                    <div className="space-y-6">
                      {/* Web Shop Info */}
                      <div className="bg-gray-700 rounded-lg p-4">
                        <h3 className="text-lg font-semibold text-white mb-2">Web Shop informacije</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-400">Naziv shopa:</span>
                            <p className="text-white">{companyOrders.webshop.shopName}</p>
                          </div>
                          <div>
                            <span className="text-gray-400">Slug:</span>
                            <p className="text-white">{companyOrders.webshop.shopSlug}</p>
                          </div>
                        </div>
                      </div>

                      {/* Orders Table */}
                      {companyOrders.orders && companyOrders.orders.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="min-w-full bg-gray-800">
                            <thead className="bg-gray-700">
                              <tr>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Tip</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Broj narudžbe</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Kupac</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Datum</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Paket</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Ukupno</th>
                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Način plaćanja</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-700">
                              {companyOrders.orders.map((order) => {
                                const customerName = order.customerId?.firstName && order.customerId?.lastName
                                  ? `${order.customerId.firstName} ${order.customerId.lastName}`
                                  : order.customerId?.firstName || 'Nepoznat kupac';
                                
                                const isSubscription = order.type === 'subscription';
                                const statusMap = {
                                  'pending': { label: 'Na čekanju', color: 'bg-yellow-600' },
                                  'processing': { label: 'U obradi', color: 'bg-blue-600' },
                                  'shipped': { label: 'Poslato', color: 'bg-purple-600' },
                                  'delivered': { label: 'Isporučeno', color: 'bg-green-600' },
                                  'paid': { label: 'Plaćeno', color: 'bg-green-600' },
                                  'verified': { label: 'Verifikovano', color: 'bg-green-600' },
                                  'cancelled': { label: 'Otkazano', color: 'bg-red-600' }
                                };
                                
                                const statusInfo = statusMap[order.status] || { label: order.status, color: 'bg-gray-600' };
                                
                                const packageMap = {
                                  'free': 'Besplatni',
                                  'standard': 'Standardni',
                                  'business': 'Biznis',
                                  'premium': 'Premium'
                                };
                                
                                return (
                                  <tr key={order._id} className="hover:bg-gray-700">
                                    <td className="px-4 py-2">
                                      <span className={`px-2 py-1 rounded text-xs ${
                                        isSubscription ? 'bg-blue-600 text-white' : 'bg-purple-600 text-white'
                                      }`}>
                                        {isSubscription ? 'Pretplata' : 'Web Shop'}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-white">#{order.orderNumber || order._id.toString().slice(-8)}</td>
                                    <td className="px-4 py-2 text-gray-300">{customerName}</td>
                                    <td className="px-4 py-2 text-gray-300">
                                      {new Date(order.createdAt).toLocaleDateString('sr-RS')}
                                    </td>
                                    <td className="px-4 py-2">
                                      <span className={`px-2 py-1 rounded text-xs text-white ${statusInfo.color}`}>
                                        {statusInfo.label}
                                      </span>
                                    </td>
                                    <td className="px-4 py-2 text-gray-300">
                                      {isSubscription && order.subscriptionPackage ? (
                                        <span className="px-2 py-1 rounded text-xs bg-gray-600 text-gray-300">
                                          {packageMap[order.subscriptionPackage] || order.subscriptionPackage}
                                        </span>
                                      ) : '-'}
                                    </td>
                                    <td className="px-4 py-2 text-gray-300">{formatCurrency(order.total || 0)}</td>
                                    <td className="px-4 py-2 text-gray-300 capitalize">
                                      {isSubscription ? 'Virman' : 
                                       order.paymentMethod === 'cash_on_delivery' ? 'Pouzećem' : 
                                       order.paymentMethod || 'N/A'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-12 text-gray-400">
                          <ShoppingCart className="w-16 h-16 mx-auto mb-4 opacity-50" />
                          <p>Ova kompanija još nema narudžbina.</p>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-12 text-gray-400">
                      <Package className="w-16 h-16 mx-auto mb-4 opacity-50" />
                      <p>Ova kompanija još nema web shop.</p>
                    </div>
                  )
                ) : (
                  <div className="text-center py-12">
                    <p className="text-gray-400">Greška pri učitavanju narudžbina</p>
                  </div>
                )}
              </div>

              <div className="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-4 flex justify-end">
                <button
                  onClick={() => {
                    setSelectedCompany(null);
                    setCompanyOrders(null);
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-md transition-colors"
                >
                  Zatvori
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompaniesListView;

